import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://melavi.in";
const DEFAULT_OG = `${SITE}/og-default.jpg`;

/**
 * SeoHead — per-route <title>, description, canonical, Open Graph and Twitter tags.
 * Emits nothing to visible DOM; only head elements via react-helmet-async.
 *
 * Props:
 *  - title      : page title (will be suffixed with " · MELAVI" unless suppressed)
 *  - description: meta description
 *  - path       : optional URL path (e.g. "/product/butterfly-bliss-pink"). Falls back to window.location.pathname.
 *  - image      : absolute or root-relative image URL for og:image / twitter:image
 *  - type       : og:type (default "website"; use "product" or "article" where useful)
 *  - noSuffix   : if true, do not append " · MELAVI"
 *  - jsonLd     : optional structured-data object (JSON-LD)
 *  - price      : optional INR price (int) — will emit product meta
 */
const SeoHead = ({
  title,
  description,
  path,
  image,
  type = "website",
  noSuffix = false,
  jsonLd,
  price,
}) => {
  const rawPath =
    path ||
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE}${rawPath === "/" ? "" : rawPath}`;
  const fullTitle = noSuffix ? title : `${title} · MELAVI`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE}${image}`
    : DEFAULT_OG;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="MELAVI" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {price != null && (
        <>
          <meta property="product:price:amount" content={String(price)} />
          <meta property="product:price:currency" content="INR" />
        </>
      )}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
